import React, { useState, useEffect, useRef } from 'react'; 
import { v4 as uuidv4 } from 'uuid';
import ReactMarkdown from 'react-markdown';
import styles from './widget_new_style.module.css';
import ContactForm from './ContactForm';
import defaultSettings from './settings.json';
import WhenClosed from './WhenClosed';

async function getChatGPTResponse(prompt, promptSettings, context, history, apiEndpoint) {
  const endpoint = apiEndpoint;  
  const message = `Settings: ${promptSettings}\n Website context: ${context}\n Chat history: \n${history}\n IMPORTANT: If you do write 2 paragraphs write it with no numbers no dots you should instead use links to what page what user can go to to learn more give the user a small amount of summarized information from the website and ask them if they want to know more. This is the end for the context this is the user message that you will respond to {EndOfContex}: \n${prompt}`;  
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 50000); // Increase timeout to 50 seconds

  try {
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        message: message
      }),
      signal: controller.signal
    });
    clearTimeout(timeoutId);
    const data = await response.json();
    let processedResponse = data.response;
    if (processedResponse) {
      processedResponse = processedResponse.replace(/\n\d+\.\s+/g, '\n\n');
      processedResponse = processedResponse.replace(/\sn\/\s/g, '\n\n');
    }
    
    return processedResponse;
  } catch (error) {
    if (error.name === 'AbortError') {
      console.error('Request timed out');
    } else {
      console.error('Error calling API:', error);
    }
    return "I am facing some issues, please try again later";
  }
}

function WidgetComponent() {
  const [settings, setSettings] = useState(defaultSettings);
  const [isOpen, setIsOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showBubble, setShowBubble] = useState(true);
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState([]);
  const [sessionId, setSessionId] = useState(uuidv4());
  const [isTyping, setIsTyping] = useState(false);
  const [showOpenButton, setShowOpenButton] = useState(true);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const messagesEndRef = useRef(null);
  const dropdownRef = useRef(null);
  const botAvatar = settings?.botAvatar;
  const firstMessage = settings?.firstMessage || "No first message provided";
  const color = settings?.color;
  const fontSize = settings?.fontSize || '14px';
  const widgetHeightMultiplier = settings?.widgetHeightMultiplier || 1;
  const widgetWidthMultiplier = settings?.widgetWidthMultiplier || 1;
  const widgetHeight = 545 * widgetHeightMultiplier;
  const widgetWidth = 335 * widgetWidthMultiplier;
  const closeIcon = "close";
  const reloadIcon = "refresh";
  const sendIcon = "send";
  const helpIcon = "help_outline";
  const typingGif = 'https://app.enqelt.com/wp-content/uploads/2024/09/typing_1.gif';
  const [bubbleClosed, setBubbleClosed] = useState(false);
  
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" })
  }
useEffect(() => {
  const currentHeight = !isOpen 
    ? (isMobile || bubbleClosed ? 105 : 250)
    : 585;
  
  const currentWidth = !isOpen 
    ? (isMobile || bubbleClosed ? 100 : 365)
    : 365;

  // Send message to parent with dimensions
  if (window.parent) {
    window.parent.postMessage({
      type: 'resize',
      height: currentHeight,
      width: currentWidth
    }, '*');
  }
}, [isOpen, isMobile, bubbleClosed]); 


  // 4. Initialize messages with first message when component mounts
  useEffect(() => {
    if (!isLoading && firstMessage && messages.length === 0) {
      setMessages([{ 
        text: firstMessage, 
        user: false, 
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      }]);
    }
  }, [isLoading, firstMessage, messages.length]);

  // 5. Load external settings
  useEffect(() => {
    const loadExternalSettings = async () => {
      try {
        // Try to fetch settings from external file
        const response = await fetch('./settings.json');
        if (response.ok) {
          const externalSettings = await response.json();
          setSettings(externalSettings);
        }
      } catch (error) {
        
      } finally {
        setIsLoading(false);
      }
    };

    loadExternalSettings();
  }, []);

  // 6. Check if device is mobile
  useEffect(() => {
    const checkIfMobile = () => {
      const mobileCheck = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      setIsMobile(mobileCheck);
    };
    checkIfMobile();
    window.addEventListener('resize', checkIfMobile);
    return () => window.removeEventListener('resize', checkIfMobile);
  }, []);

  // 7. Handle chat link clicks
  useEffect(() => {
    const handleChatLinkClicks = (event) => {
      let target = event.target;
      while (target && target.tagName !== 'A' && target.parentElement) {
        target = target.parentElement;
      }

      if (target && target.tagName === 'A') {
        event.preventDefault();
        event.stopPropagation();
        
        const href = target.getAttribute('href');        
        if (href && !href.startsWith('#')) {
          window.open(href, '_blank');
        }
      }
    };
  
    const chatContainer = document.getElementById('chats');
    if (chatContainer) {
      chatContainer.addEventListener('click', handleChatLinkClicks);
    }
    
    // Cleanup
    return () => {
      const chatContainer = document.getElementById('chats');
      if (chatContainer) {
        chatContainer.removeEventListener('click', handleChatLinkClicks);
      }
    };
  }, [isOpen]);
  
  // 8. Auto-scroll when typing
  useEffect(() => {
    if (isTyping) {
      scrollToBottom();
    }
  }, [isTyping]);

  // 9. Handle clicks outside dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };
  
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // 10. Scroll to bottom when messages change
  useEffect(scrollToBottom, [messages]);

  const close = () => {
    setIsOpen(false);
    setShowOpenButton(true); 
    setDropdownOpen(false);
  };
  
  const open = () => {
    setIsOpen(true);
    setShowOpenButton(false); 
  };

  const restart = () => {
    setInput('');
    setMessages([{ text: firstMessage, user: false, timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) }]);
    setIsTyping(false);
    setIsOpen(true);
    setDropdownOpen(false);
    setSessionId(uuidv4());
  };

const GetResponseFromAPI = async (prompt) => {
  try {
    // Log the API endpoint from settings for debugging
    const history = messages.map(msg => `${msg.user ? 'User' : 'Bot'}: ${msg.text}`).join('\n');
    const response = await getChatGPTResponse(
      prompt, 
      settings.promptSettings, 
      settings.contextText, 
      history,
      settings.apiEndpoint
    );
    return typeof response === 'string' ? response : JSON.stringify(response);
  } catch (error) {
    console.error("API Error:", error);
    return "I am facing some issues, please try again later";
  }
};
  const onSubmit = async (formData) => {
    const formDataString = `Namn: ${formData.name}, Efternamn: ${formData.lastName}, Telefonnummer: ${formData.phoneNumber}, Epost: ${formData.email}`;
    setIsTyping(true);
    const response = await GetResponseFromAPI(formDataString);
    setIsTyping(false);
    setMessages(prevMessages => [...prevMessages, { text: response, user: false, timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) }]);
  };

  const handleSubmit = async () => {
    if (input.trim() === '') return;
    
    setMessages(prevMessages => [...prevMessages, { text: input, user: true, timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) }]);
    const userInput = input;
    setInput('');
    setIsTyping(true);
    const response = await GetResponseFromAPI(userInput);
    setIsTyping(false);
    setMessages(prevMessages => [...prevMessages, { text: response, user: false, timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) }]);
  };

  const handleKeyPress = async (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      await handleSubmit();
    }
  };


  return (
    <>
      {!isOpen ? (
          <WhenClosed open={open} settings={settings} bubble={showBubble} setBubbleClosed={setBubbleClosed} />
      ) : (
        <div className={styles.widget} style={{ 
          fontSize, 
          height: `${widgetHeight}px`, 
          width: `${widgetWidth}px`, 
          display: 'flex', 
          flexDirection: 'column',
          borderRadius: '16px',
          boxShadow: '0 4px 20px 0 rgba(0, 0, 0, 0.1), 0 8px 16px 0 rgba(0, 0, 0, 0.1)'
        }}>
        <link href="https://fonts.googleapis.com/icon?family=Material+Icons" rel="stylesheet"></link>
        <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600&display=swap" rel="stylesheet"></link>
        
        {/* Updated Header Design */}
        <div className={`${styles.chat_header} drag-handle`} style={{ 
          display: 'flex', 
          flexDirection: 'column',
          backgroundColor: color,
          borderRadius: '16px 16px 0 0',
          padding: '16px',
          boxShadow: '0 2px 10px rgba(0, 0, 0, 0.08)'
        }}>
          {/* Top section with buttons */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%',
            marginBottom: '12px'
          }}>
            {/* Left side - Bot info */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
            }}>
              <img 
                src={botAvatar} 
                alt="Assistant" 
                style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  marginRight: '12px',
                  border: '2px solid rgba(255, 255, 255, 0.3)'
                }}
              />
              <div style={{
                display: 'flex',
                flexDirection: 'column'
              }}>
                <span style={{
                  color: 'white',
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: '600',
                  fontSize: '15px'
                }}>
                  AI Assistant
                </span>
                <span style={{
                  color: 'rgba(255, 255, 255, 0.8)',
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: '400',
                  fontSize: '12px'
                }}>
                  Online
                </span>
              </div>
            </div>
            
            {/* Right side - Control buttons */}
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <button 
                onClick={restart} 
                title="Start new conversation"
                style={{ 
                  background: 'rgba(255, 255, 255, 0.15)', 
                  border: 'none', 
                  marginRight: '8px', 
                  cursor: 'pointer',
                  padding: '6px',
                  borderRadius: '50%',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  transition: 'background 0.2s ease'
                }}
                onMouseOver={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.25)'}
                onMouseOut={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)'}
              >
                <i className="material-icons" style={{ color: 'white', fontSize: '18px' }}>{reloadIcon}</i>
              </button>
              <button 
                onClick={close} 
                title="Close chat"
                style={{ 
                  background: 'rgba(255, 255, 255, 0.15)', 
                  border: 'none', 
                  cursor: 'pointer',
                  padding: '6px',
                  borderRadius: '50%',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  transition: 'background 0.2s ease'
                }}
                onMouseOver={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.25)'}
                onMouseOut={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)'}
              >
                <i className="material-icons" style={{ color: 'white', fontSize: '18px' }}>{closeIcon}</i>
              </button>
            </div>
          </div>
        </div>
        
        {/* Chat messages area */}
        <div className={styles.chats} id="chats" style={{ 
          flex: 1, 
          overflowY: 'auto',
          padding: '12px',
          backgroundColor: '#f5f5f7'
        }}>
          {messages.map((message, index) => (
            <div key={index}>
              {message.user ? (
                <div className={styles.userMsgContainer} style={{ marginBottom: '16px' }}>
                  <p className={styles.userMsg} style={{ 
                    backgroundColor: color,
                    borderRadius: '18px 4px 18px 18px',
                    padding: '12px 16px',
                    marginBottom: '4px',
                    boxShadow: '0 2px 6px rgba(0, 0, 0, 0.1)',
                    fontFamily: 'Inter, sans-serif'
                  }}>{message.text}</p>
                  <div className={styles.timestampBox}>
                    <p className={styles.userTimestamp} style={{ fontSize: '10px' }}>{message.timestamp}</p>
                  </div>
                </div>
              ) : (
                <div className={styles.botMsgContainer} style={{ marginBottom: '16px' }}>
                  <img className={styles.botAvatar} src={botAvatar} alt="Bot avatar" style={{
                    marginRight: '8px',
                    width: '32px',
                    height: '32px'
                  }} />
                  <ReactMarkdown className={`${styles.botMsg} selectable-text`} 
                    components={{
                      a: ({node, ...props}) => <a 
                        style={{ color: color }} 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        {...props} 
                      />
                    }}
                    style={{
                      borderRadius: '4px 18px 18px 18px',
                      padding: '12px 16px',
                      marginBottom: '4px',
                      backgroundColor: 'white',
                      boxShadow: '0 2px 6px rgba(0, 0, 0, 0.05)',
                      fontFamily: 'Inter, sans-serif'
                    }}
                  >{message.text.replace('{contact_form}', '')}</ReactMarkdown>
                  <div className={styles.timestampBox}>
                    <p className={styles.botTimestamp} style={{ fontSize: '10px' }}>{message.timestamp}</p>
                  </div>
                  {message.text.includes('{contact_form}') && (
                    <div>
                      <img className={styles.botAvatar} src={botAvatar} alt="Bot avatar" />
                      <ContactForm onSubmit={onSubmit} />
                      <div className={styles.clearfix}></div>
                    </div>
                  )}
                </div>
              )}
              <div className={styles.clearfix}></div>
            </div>
          ))}
          {isTyping && (
            <div style={{ display: 'flex', alignItems: 'center', marginTop: '4px', marginBottom: '16px' }}>
              <img className={styles.botAvatar} src={botAvatar} alt="Bot avatar" style={{
                width: '32px',
                height: '32px',
                marginRight: '8px'
              }} />
              <img src={typingGif} alt="Typing..." style={{ 
                width: '60px', 
                height: '38px', 
                marginLeft: '8px',
                padding: '4px 8px',
                backgroundColor: 'white',
                borderRadius: '12px',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)'
              }} />
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
        
        {/* Input area */}
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          padding: '12px 16px',
          backgroundColor: 'white',
          borderTop: '1px solid #eaeaea'
        }}>
          <div style={{ 
            display: 'flex',
            alignItems: 'center',
            width: '100%',
            position: 'relative',
            borderRadius: '24px',
            backgroundColor: '#f0f0f0',
            padding: '4px 4px 4px 16px',
            boxShadow: 'inset 0 1px 3px rgba(0, 0, 0, 0.05)'
          }}>
            <input 
              type="text" 
              id="userInput" 
              placeholder="Type your message..." 
              className={`${styles.usrInput} selectable-text`} 
              value={input} 
              onChange={event => setInput(event.target.value)} 
              onKeyPress={handleKeyPress} 
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck="false"
              data-form-type="other"
              style={{ 
                fontSize,
                flex: 1,
                border: 'none',
                background: 'transparent',
                fontFamily: 'Inter, sans-serif',
                padding: '1px 0'
              }} 
            />
            <button 
              onClick={handleSubmit}
              disabled={input.trim() === ''}
              style={{
                background: color,
                color: 'white',
                border: 'none',
                borderRadius: '50%',
                width: '36px',
                height: '36px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: input.trim() === '' ? 'default' : 'pointer',
                opacity: input.trim() === '' ? 0.6 : 1,
                transition: 'all 0.2s ease'
              }}
            >
              <i className="material-icons" style={{ fontSize: '18px' }}>{sendIcon}</i>
            </button>
          </div>
        </div>
        
        {/* Footer */}
        <div style={{ 
            textAlign: 'center', 
            padding: '8px', 
            fontSize: '11px', 
            color: '#888', 
            fontFamily: 'Inter, sans-serif',
            borderTop: '1px solid #eaeaea',
            backgroundColor: 'white',
            borderRadius: '0 0 16px 16px'
          }}>
            Powered by <a href="https://enqelt.com" target="_blank" rel="noopener noreferrer" style={{ 
              color: color, 
              textDecoration: 'none',
              fontWeight: 500
            }}>Enqelt</a>
          </div>
        </div>
      )}
    </>
  );
}

export default WidgetComponent;